import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import {
  ArrowRightIcon,
  PlayCircleIcon,
  CreditCardIcon,
  TrendingUpIcon,
  ShieldCheckIcon,
  Zap,
  Globe,
  Lock,
  Sparkles,
  Users,
  Activity,
  Wallet
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
// Optimized CSS - Instant theme transitions
const optimizedStyles = `
  .instant-theme * {
    transition: background-color 0.2s ease, 
                color 0.2s ease, 
                border-color 0.2s ease !important;
  }
  
  .hover-quick {
    transition: transform 0.15s ease, opacity 0.15s ease !important;
  }
`;

// Floating particles component - Optimized
function FloatingParticles({ isDark }) {
  const particlesRef = useRef([]);

  useEffect(() => {
    const particles = particlesRef.current.filter(Boolean);

    particles.forEach((particle, i) => {
      gsap.to(particle, {
        y: `random(-30, 30)`,
        x: `random(-30, 30)`,
        duration: `random(4, 6)`,
        repeat: -1,
        yoyo: true,
        ease: 'none',
        delay: i * 0.2
      });

      gsap.to(particle, {
        opacity: `random(0.2, 0.8)`,
        duration: `random(2, 4)`,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });
    });
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(1)].map((_, i) => (
        <div
          key={i}
          ref={el => particlesRef.current[i] = el}
          className={`absolute w-1 h-1 rounded-full ${isDark ? 'bg-cyan-400/30' : 'bg-cyan-500/20'
            }`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            filter: 'blur(0.5px)'
          }}
        />
      ))}
    </div>
  );
}

// Animated gradient orbs - Optimized
function GradientOrbs({ isDark }) {
  const orb1Ref = useRef(null);

  useEffect(() => {
    let rafId = null;

    const handleMouseMove = (e) => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        const { clientX, clientY } = e;
        const xPos = (clientX / window.innerWidth - 0.5) * 20;
        const yPos = (clientY / window.innerHeight - 0.5) * 20;

        if (orb1Ref.current) {
          gsap.to(orb1Ref.current, {
            x: xPos * 0.5,
            y: yPos * 0.5,
            duration: 0.6,
            ease: "power2.out",
          });
        }

        rafId = null;
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
      <div
        ref={orb1Ref}
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)'
        }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full blur-3xl"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        className="absolute bottom-1/4 left-1/2 w-72 h-72 rounded-full blur-3xl"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%)'
        }}
      />
    </div>
  );
}

// Enhanced 3D Graphic - Optimized
function Hero3DGraphic() {
  const { isDark } = useTheme();
  const dashboardRef = useRef(null);
  const securityRef = useRef(null);
  const notificationRef = useRef(null);
  const statsRef = useRef(null);
  const cardRef = useRef(null);
  const chartBarsRef = useRef([]);

  useEffect(() => {
    // Dashboard card animation
    if (dashboardRef.current) {
      gsap.fromTo(
        dashboardRef.current,
        { opacity: 0, rotationY: 25, rotationX: -10, y: 50, scale: 0.9 },
        { opacity: 0.7, rotationY: 5, rotationX: -2, y: 0, scale: 1, duration: 1.5, ease: 'power3.out', delay: 0.3 }
      );

      gsap.to(dashboardRef.current, {
        y: -15, rotationY: 8, duration: 8, repeat: -1, yoyo: true, ease: 'sine.inOut',
        opacity: 0.7 // yoyo animation ke liye bhi
      });
    }

    if (securityRef.current) {
      gsap.fromTo(
        securityRef.current,
        { opacity: 0, x: -60, y: 30, scale: 0.8, rotation: -5 },
        { opacity: 0.7, x: 0, y: 0, scale: 1, rotation: 0, duration: 1.2, ease: 'back.out(1.4)', delay: 0.8 }
      );
      gsap.to(securityRef.current, { y: -10, rotation: 2, duration: 6, repeat: -1, yoyo: true, ease: 'sine.inOut', opacity: 0.7 });
    }

    if (notificationRef.current) {
      gsap.fromTo(
        notificationRef.current,
        { opacity: 0, x: 60, y: -30, scale: 0.8 },
        { opacity: 0.7, x: 0, y: 0, scale: 1, duration: 1.2, ease: 'back.out(1.4)', delay: 1 }
      );
      gsap.to(notificationRef.current, { y: -8, duration: 2.5, repeat: -1, yoyo: true, ease: 'sine.inOut', opacity: 0.7 });
    }

    if (statsRef.current) {
      gsap.fromTo(
        statsRef.current,
        { opacity: 0, x: 60, y: 40, scale: 0.85 },
        { opacity: 0.7, x: 0, y: 0, scale: 1, duration: 1.3, ease: 'back.out(1.3)', delay: 1.2 }
      );
      gsap.to(statsRef.current, { y: -12, duration: 5.5, repeat: -1, yoyo: true, ease: 'sine.inOut', opacity: 0.7 });
    }

    if (cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, x: -50, y: -40, rotation: -8 },
        { opacity: 0.7, x: 0, y: 0, rotation: -3, duration: 1.4, ease: 'back.out(1.2)', delay: 1.4 }
      );
      gsap.to(cardRef.current, { y: -10, rotation: -1, duration: 4.2, repeat: -1, yoyo: true, ease: 'sine.inOut', opacity: 0.7 });
    }

    chartBarsRef.current.forEach((bar, i) => {
      if (bar) {
        gsap.fromTo(
          bar,
          { scaleY: 0, transformOrigin: 'bottom', opacity: 0.7 }, // chart bars opacity
          { scaleY: 1, duration: 0.8, ease: 'back.out(1.5)', delay: 1.2 + i * 0.05, opacity: 0.7 }
        );
      }
    });
  }, []);


  return (
    <div className="relative w-full h-[400px] sm:h-[500px] md:h-[600px]" style={{ perspective: '1400px' }}>
      {/* Main dashboard card */}
      <div
        ref={dashboardRef}
        className="absolute top-12 left-4 right-4 md:left-8 md:right-8"
        style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
      >
        <div
          className={`rounded-3xl p-8 border-2 ${isDark
            ? 'bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-cyan-500/20'
            : 'bg-gradient-to-br from-white/95 to-slate-50/95 border-slate-300/50'
            }`}
          style={{
            boxShadow: isDark
              ? '0 25px 50px -12px rgba(6, 182, 212, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)'
              : '0 25px 50px -12px rgba(0, 0, 0, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.8)'
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className={`w-4 h-4 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`} />
                <div
                  className={`absolute inset-0 w-4 h-4 rounded-full animate-ping ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`}
                  style={{ animationDuration: '2s' }}
                />
              </div>
              <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Payment Analytics
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${isDark
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                  }`}
              >
                Real-time
              </span>
            </div>
          </div>

          <div className="mb-6">
            <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Total Revenue (30 days)
            </p>
            <div className="flex items-baseline gap-3">
              <span
                className={`text-5xl font-black tracking-tight ${isDark
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600'
                  }`}
              >
                $284,392
              </span>
              <div className="flex items-center gap-1.5">
                <TrendingUpIcon className="w-5 h-5 text-emerald-500" />
                <span className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  +12.5%
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-end gap-2 h-24 mb-6">
            {[40, 65, 45, 80, 55, 90, 70, 95, 60, 85, 75, 100].map((h, i) => (
              <div
                key={i}
                ref={el => chartBarsRef.current[i] = el}
                className={`flex-1 rounded-t-lg hover-quick hover:opacity-80 cursor-pointer ${i >= 10
                  ? isDark
                    ? 'bg-gradient-to-t from-cyan-500 to-cyan-400'
                    : 'bg-gradient-to-t from-cyan-600 to-cyan-500'
                  : isDark
                    ? 'bg-gradient-to-t from-cyan-500/30 to-cyan-400/20'
                    : 'bg-gradient-to-t from-cyan-200 to-cyan-100'
                  }`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>

          <div className="space-y-3">
            {[
              { icon: CreditCardIcon, title: 'Visa •••• 4242', time: '2 hours ago', amount: '+$1,240.00', color: 'cyan' },
              { icon: TrendingUpIcon, title: 'Wire Transfer', time: '5 hours ago', amount: '+$8,500.00', color: 'violet' }
            ].map((item, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between py-3 px-4 rounded-xl hover-quick hover:scale-[1.02] cursor-pointer ${isDark
                  ? 'bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.05]'
                  : 'bg-slate-100/80 hover:bg-slate-100 border border-slate-200/50'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color === 'cyan'
                      ? isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'
                      : isDark ? 'bg-violet-500/20' : 'bg-violet-100'
                      }`}
                  >
                    <item.icon
                      className={`w-5 h-5 ${item.color === 'cyan'
                        ? isDark ? 'text-cyan-400' : 'text-cyan-600'
                        : isDark ? 'text-violet-400' : 'text-violet-600'
                        }`}
                    />
                  </div>
                  <div>
                    <span className={`text-sm font-bold block ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                      {item.title}
                    </span>
                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                      {item.time}
                    </span>
                  </div>
                </div>
                <span className={`text-sm font-black ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  {item.amount}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating cards */}
      <div ref={securityRef} className="absolute bottom-16 left-2 md:left-4 z-10" style={{ transformStyle: 'preserve-3d' }}>
        <div
          className={`rounded-2xl px-5 py-4 border-2 flex items-center gap-4 ${isDark ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-cyan-500/30'
            : 'bg-gradient-to-br from-white to-slate-50 border-cyan-300'
            }`}
          style={{ backdropFilter: 'blur(20px)' }}
        >
          <div className={`p-3 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
            <ShieldCheckIcon className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
          </div>
          <div>
            <p className={`text-sm font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>PCI DSS Level 1</p>
            <p className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Bank-grade encryption</p>
          </div>
        </div>
      </div>

      <div ref={notificationRef} className="absolute top-4 right-2 md:right-8 z-10">
        <div
          className={`rounded-2xl px-5 py-3 border-2 flex items-center gap-3 ${isDark ? 'bg-gradient-to-br from-slate-900/95 to-emerald-900/30 border-emerald-500/30'
            : 'bg-gradient-to-br from-white to-emerald-50 border-emerald-300'
            }`}
          style={{ backdropFilter: 'blur(20px)' }}
        >
          <div className="relative">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-ping" />
          </div>
          <span className={`text-sm font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>Payment Processed</span>
        </div>
      </div>

      <div ref={statsRef} className="absolute bottom-32 right-2 md:right-4 z-10">
        <div
          className={`rounded-2xl px-5 py-4 border-2 ${isDark ? 'bg-gradient-to-br from-slate-900/95 to-violet-900/30 border-violet-500/30'
            : 'bg-gradient-to-br from-white to-violet-50 border-violet-300'
            }`}
          style={{ backdropFilter: 'blur(20px)' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
              <Users className={`w-5 h-5 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
            </div>
            <div>
              <p className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Active Users</p>
              <p className={`text-xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>12,459</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-emerald-500" />
            <span className={`text-xs font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>+23% this week</span>
          </div>
        </div>
      </div>

      <div ref={cardRef} className="absolute top-24 left-2 md:left-6 z-10">
        <div
          className={`rounded-2xl p-5 border-2 w-64 ${isDark ? 'bg-gradient-to-br from-slate-900/95 via-blue-900/40 to-cyan-900/40 border-cyan-500/30'
            : 'bg-gradient-to-br from-white via-blue-50 to-cyan-50 border-cyan-300'
            }`}
          style={{ backdropFilter: 'blur(20px)' }}
        >
          <div className="flex justify-between items-start mb-8">
            <Sparkles className={`w-6 h-6 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
            <Wallet className={`w-6 h-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
          </div>
          <div className="space-y-1 mb-4">
            <p className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Available Balance</p>
            <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>$45,892.00</p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex-1 h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
              <div className="w-3/4 h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500" />
            </div>
            <span className={`text-xs font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>75%</span>
          </div>
        </div>
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[500px] md:h-[500px] rounded-full border-2 border-cyan-500/10 pointer-events-none animate-ping" style={{ animationDuration: '4s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] md:w-[450px] md:h-[450px] rounded-full border border-violet-500/10 pointer-events-none" />
    </div>
  );
}

export function Hero() {
  const { isDark } = useTheme();
  const heroRef = useRef(null);
  const badgeRef = useRef(null);
  const headlineRef = useRef(null);
  const descriptionRef = useRef(null);
  const ctaRef = useRef(null);
  const trustRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(badgeRef.current, { opacity: 0, y: -30, scale: 0.8 }, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'back.out(1.7)' });
    tl.fromTo(headlineRef.current, { opacity: 0, y: 50, scale: 0.95 }, { opacity: 1, y: 0, scale: 1, duration: 1, ease: 'power3.out' }, '-=0.4');
    tl.fromTo(descriptionRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8 }, '-=0.6');

    if (ctaRef.current) {
      tl.fromTo(ctaRef.current.children, { opacity: 0, y: 30, scale: 0.9 }, { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.15, ease: 'back.out(1.5)' }, '-=0.4');
    }

    if (trustRef.current) {
      tl.fromTo(trustRef.current.children, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.1 }, '-=0.3');
    }

    let rafId = null;
    const handleMouseMove = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const xPos = (e.clientX / window.innerWidth - 0.5) * 20;
        const yPos = (e.clientY / window.innerHeight - 0.5) * 20;
        gsap.to(heroRef.current, { rotationY: xPos * 0.08, rotationX: -yPos * 0.08, duration: 0.6, ease: "power2.out" });
        rafId = null;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <style>{optimizedStyles}</style>
      <section className="instant-theme relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-28 pb-20 overflow-hidden">
        <GradientOrbs isDark={isDark} />
        <FloatingParticles isDark={isDark} />

        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: isDark
              ? 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)'
              : 'linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />

        <div className="max-w-7xl w-full relative flex justify-center items-center" ref={heroRef} style={{ willChange: "transform" }}>
          <div className="absolute inset-0 flex justify-center items-center -z-10">
            <div className="w-full max-w-5xl md:max-w-6xl opacity-40 scale-[0.85] sm:scale-[1.0] md:scale-[1.15] lg:scale-[1.25]">
              <Hero3DGraphic />
            </div>
          </div>

          <div className="text-center max-w-4xl z-10">
            <div ref={badgeRef}>
              <div
                className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-sm font-bold mb-10 hover-quick hover:scale-105 cursor-pointer ${isDark
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border-2 border-cyan-500/30'
                  : 'bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-2 border-cyan-300'
                  }`}
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500" />
                </span>
                <Zap className="w-4 h-4" />
                Now processing $12B+ annually
                <Sparkles className="w-4 h-4" />
              </div>
            </div>

            <h1 ref={headlineRef} className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-[-0.02em] leading-[1.1] mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              The payment{' '}
              <span className={`relative inline-block font-semibold ${isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600'
                }`}>
                infrastructure
                <div className={`absolute -inset-1 blur-xl -z-10 ${isDark ? 'bg-gradient-to-r from-cyan-500/15 via-blue-500/15 to-violet-500/15' : 'bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-violet-500/10'
                  }`} />
              </span>
              <br />
              built for{' '}
              <span className="relative inline-block font-semibold">
                scale
                <svg className="absolute -bottom-1 left-0 w-full" height="8" viewBox="0 0 300 8" fill="none">
                  <path d="M2 6C50 2 100 1 150 3C200 5 250 6 298 4" stroke={isDark ? '#06b6d4' : '#0891b2'} strokeWidth="3" strokeLinecap="round" className="animate-pulse" />
                </svg>
              </span>
            </h1>

            <p ref={descriptionRef} className={`text-lg md:text-xl mb-10 leading-relaxed max-w-2xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Accept payments globally with a single integration. Built for developers,
              designed for businesses. Move money at the{' '}
              <span className={`font-semibold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>speed of the internet.</span>
            </p>


            <div
              ref={ctaRef}
              className="flex flex-col sm:flex-row justify-center gap-4 mb-16 w-full max-w-[280px] sm:max-w-none mx-auto"
            >
              {/* Start Today Button */}
              <motion.a
                href="#premiumcta"
                className={`group relative inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3.5 sm:py-4 text-base sm:text-lg font-black rounded-2xl w-full sm:w-auto max-w-full sm:max-w-none overflow-hidden ${isDark
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                  : 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white'
                  }`}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <Sparkles
                  className="w-5 h-5 group-hover:rotate-12 transition-transform duration-500"
                />
                Start Today
                <ArrowRightIcon
                  className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-500"
                />
              </motion.a>

              {/* Watch Demo Button */}
              <motion.a
                href="#about"
                className={`group inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3.5 sm:py-4 text-base sm:text-lg font-black rounded-2xl w-full sm:w-auto max-w-full sm:max-w-none border-2 ${isDark
                  ? 'text-slate-200 hover:text-white bg-white/[0.05] hover:bg-white/[0.1] border-white/[0.1] hover:border-cyan-500/40'
                  : 'text-slate-800 hover:text-slate-900 bg-white/90 hover:bg-white border-slate-300 hover:border-cyan-400'
                  }`}
                style={{ backdropFilter: 'blur(10px)' }}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <PlayCircleIcon
                  className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-500"
                />
                Watch Demo
                <Globe
                  className="w-4 h-5 sm:w-5 sm:h-5 group-hover:rotate-12 transition-transform duration-500"
                />
              </motion.a>
            </div>
            <div ref={trustRef} className={`flex flex-wrap items-center justify-center gap-8 text-sm font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {[
                { Icon: ShieldCheckIcon, text: 'PCI DSS Level 1', color: 'emerald' },
                { Icon: Lock, text: 'SOC 2 Type II', color: 'blue' },
                { Icon: Activity, text: '99.99% Uptime', color: 'violet' },
                { Icon: Globe, text: '135+ Countries', color: 'cyan' }
              ].map((item, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <div className={`w-px h-6 ${isDark ? 'bg-slate-700' : 'bg-slate-300'}`} />}
                  <div className="flex items-center gap-2.5 group cursor-pointer">
                    <div className={`p-2 rounded-lg hover-quick group-hover:scale-110 ${isDark ? `bg-${item.color}-500/10` : `bg-${item.color}-100`
                      }`}>
                      <item.Icon className={`w-4 h-4 ${isDark ? `text-${item.color}-400` : `text-${item.color}-600`}`} />
                    </div>
                    <span className={`group-hover:text-${item.color}-500`} style={{ transition: 'color 0.2s' }}>{item.text}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}